import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { ChevronRight, ChevronLeft, Check, Loader2 } from 'lucide-react';

const COUNTRIES = ['Australia','United Kingdom','United States','Canada','New Zealand','Ireland','South Africa','India','Singapore','Germany','France','Netherlands','Other'];
const STUDENT_STOPS = [1,2,3,5,8,10,15,20,25,30,40,50,75,100,150,200,300,500,1000,2000,5000];
const TUTOR_STOPS = [1,2,3,5,8,10,15,20,25,30,40,50,75,100,150,200,300,500,1000];
const PROVIDERS = [
  { id: 'lessonspace', label: 'LessonSpace' },
  { id: 'zoom', label: 'Zoom' },
  { id: 'googlemeet', label: 'Google Meet' },
  { id: 'msteams', label: 'Microsoft Teams' },
  { id: 'other', label: 'Other' },
];

const Onboarding = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showWelcome, setShowWelcome] = useState(false);

  const [form, setForm] = useState({
    email: '', companyName: '', country: '',
    fullName: '', password: '', passwordConfirm: '',
    approxStudents: 20, approxTutors: 5,
    provider: '', otherProvider: '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const nearestStop = (val, stops) => stops.reduce((prev, curr) => Math.abs(curr - val) < Math.abs(prev - val) ? curr : prev);
  const toSliderPos = (val, stops) => { const idx = stops.indexOf(nearestStop(val, stops)); return idx >= 0 ? (idx / (stops.length - 1)) * 100 : 50; };
  const fromSliderPos = (pos, stops) => { const idx = Math.round((pos / 100) * (stops.length - 1)); return stops[Math.max(0, Math.min(stops.length - 1, idx))]; };

  const canNext = () => {
    if (step === 1) return form.email && form.companyName && form.country;
    if (step === 2) return form.fullName && form.password.length >= 6 && form.password === form.passwordConfirm;
    if (step === 3) return true;
    if (step === 4) return form.provider;
    return true;
  };

  const handleNext = async () => {
    setError('');
    if (step === 2) {
      setLoading(true);
      try {
        const { error: signUpErr } = await supabase.auth.signUp({
          email: form.email,
          password: form.password,
          options: { data: { full_name: form.fullName } },
        });
        if (signUpErr) throw signUpErr;
      } catch (err) {
        setError(err.message || 'Signup failed');
        setLoading(false);
        return;
      }
      setLoading(false);
    }
    if (step === 4) {
      setShowWelcome(true);
      setTimeout(() => navigate('/'), 4000);
      return;
    }
    setStep(s => s + 1);
  };

  const cardClass = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;
  const inputClass = `w-full px-4 py-2.5 text-sm rounded-lg border ${isDark ? 'bg-[#18181b] border-[#27272a] text-white placeholder-[#52525b]' : 'bg-white border-[#e5e5e5] text-[#1a1a1a] placeholder-[#999]'}`;

  if (showWelcome) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#09090b]' : 'bg-[#fafafa]'}`}>
        <div className="text-center animate-fade-in">
          <div className="w-20 h-20 rounded-full accent-gradient mx-auto mb-6 flex items-center justify-center">
            <Check className="h-10 w-10 text-white" />
          </div>
          <h1 className={`text-3xl font-semibold font-[Manrope] mb-2 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Welcome to TutorTracer</h1>
          <p className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Setting up your workspace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${isDark ? 'bg-[#09090b]' : 'bg-[#fafafa]'}`}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img
            src={isDark ? '/tutortracer_logowithtext_white_trpt.png' : '/tutortracer_logowithtext_black_trpt.png'}
            alt="TutorTracer"
            className="h-8"
          />
        </div>

        <div className={`${cardClass} p-6`}>
          {error && (
            <div className="mb-4 px-3 py-2 rounded-lg text-xs bg-red-500/10 text-red-400 border border-red-500/20">
              {error}
            </div>
          )}

          {/* Step 1: Company Info */}
          {step === 1 && (
            <div className="space-y-4">
              <h2 className={`text-lg font-semibold font-[Manrope] ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Get Started</h2>
              <div>
                <label className={`text-xs block mb-1.5 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Email</label>
                <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="you@company.com" className={inputClass} />
              </div>
              <div>
                <label className={`text-xs block mb-1.5 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Company Name</label>
                <input value={form.companyName} onChange={e => set('companyName', e.target.value)} placeholder="Your tutoring company" className={inputClass} />
              </div>
              <div>
                <label className={`text-xs block mb-1.5 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Country</label>
                <select value={form.country} onChange={e => set('country', e.target.value)} className={inputClass}>
                  <option value="">Select country</option>
                  {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* Step 2: Account */}
          {step === 2 && (
            <div className="space-y-4">
              <h2 className={`text-lg font-semibold font-[Manrope] ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Create Account</h2>
              <div>
                <label className={`text-xs block mb-1.5 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Full Name</label>
                <input value={form.fullName} onChange={e => set('fullName', e.target.value)} placeholder="Your name" className={inputClass} />
              </div>
              <div>
                <label className={`text-xs block mb-1.5 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Password</label>
                <input type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="Min 6 characters" className={inputClass} />
              </div>
              <div>
                <label className={`text-xs block mb-1.5 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Confirm Password</label>
                <input type="password" value={form.passwordConfirm} onChange={e => set('passwordConfirm', e.target.value)} placeholder="Confirm password" className={inputClass} />
                {form.passwordConfirm && form.password !== form.passwordConfirm && (
                  <p className="text-xs text-red-400 mt-1">Passwords don't match</p>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Company Size */}
          {step === 3 && (
            <div className="space-y-6">
              <h2 className={`text-lg font-semibold font-[Manrope] ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Company Size</h2>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className={`text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Approximate Students</label>
                  <input
                    type="number"
                    value={form.approxStudents}
                    onChange={e => set('approxStudents', parseInt(e.target.value) || 0)}
                    className={`w-20 px-2 py-1 text-sm text-center rounded border ${isDark ? 'bg-[#18181b] border-[#27272a] text-white' : 'bg-white border-[#e5e5e5] text-[#1a1a1a]'}`}
                  />
                </div>
                <input
                  type="range" min={0} max={100}
                  value={toSliderPos(form.approxStudents, STUDENT_STOPS)}
                  onChange={e => set('approxStudents', fromSliderPos(parseInt(e.target.value), STUDENT_STOPS))}
                  className="w-full accent-[#586f8d]"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className={`text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Approximate Tutors</label>
                  <input
                    type="number"
                    value={form.approxTutors}
                    onChange={e => set('approxTutors', parseInt(e.target.value) || 0)}
                    className={`w-20 px-2 py-1 text-sm text-center rounded border ${isDark ? 'bg-[#18181b] border-[#27272a] text-white' : 'bg-white border-[#e5e5e5] text-[#1a1a1a]'}`}
                  />
                </div>
                <input
                  type="range" min={0} max={100}
                  value={toSliderPos(form.approxTutors, TUTOR_STOPS)}
                  onChange={e => set('approxTutors', fromSliderPos(parseInt(e.target.value), TUTOR_STOPS))}
                  className="w-full accent-[#586f8d]"
                />
              </div>
            </div>
          )}

          {/* Step 4: Platform */}
          {step === 4 && (
            <div className="space-y-4">
              <h2 className={`text-lg font-semibold font-[Manrope] ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Virtual Classroom</h2>
              <p className={`text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Which platform do you use for online lessons?</p>
              <div className="grid grid-cols-1 gap-2">
                {PROVIDERS.map(p => (
                  <button
                    key={p.id}
                    onClick={() => set('provider', p.id)}
                    className={`px-4 py-3 text-sm text-left rounded-lg border transition-colors ${
                      form.provider === p.id
                        ? isDark ? 'border-[#7662ac] bg-[#7662ac]/10 text-white' : 'border-[#586f8d] bg-[#586f8d]/10 text-[#1a1a1a]'
                        : isDark ? 'border-[#27272a] text-[#a1a1aa] hover:border-[#3f3f46]' : 'border-[#e5e5e5] text-[#666] hover:border-[#d0d0d0]'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              {form.provider === 'other' && (
                <input value={form.otherProvider} onChange={e => set('otherProvider', e.target.value)} placeholder="Please specify" className={inputClass} />
              )}
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            {step > 1 ? (
              <button onClick={() => setStep(s => s - 1)} className={`flex items-center gap-1 text-sm ${isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666] hover:text-[#1a1a1a]'}`}>
                <ChevronLeft className="h-4 w-4" /> Back
              </button>
            ) : <div />}
            <button
              onClick={handleNext}
              disabled={!canNext() || loading}
              className={`flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-40 ${isDark ? 'bg-white text-black hover:bg-[#e5e5e5]' : 'bg-[#1a1a1a] text-white hover:bg-[#333]'}`}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {step === 4 ? 'Finish' : 'Continue'}
              {!loading && step < 4 && <ChevronRight className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Step indicator */}
        <div className={`text-center mt-4 text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
          Step {step} of 4
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
